import {
  Button,
  EventNoteIcon,
  Grid,
  IconButton,
  makeStyles,
  MessageIcon,
  NoteIcon,
  PlaceIcon,
  SvgIcon,
  SvgIconProps,
  Theme,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import cn from 'classnames'
import { useObserver } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'
import { useI18n } from 'utils/localisation'

import { TActivityFiltersModel } from './ActivityFilterModel'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    margin: theme.spacing(7.5, 0, 6),
  },
  filterHelpText: {
    margin: theme.spacing(0, 2, 1, 0.5),
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
  filterButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  unselected: {
    color: theme.palette.action.disabled,
  },
  filterButtons: {
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
      marginLeft: theme.spacing(1),
    },
  },
}))

const FilterIconMap: Record<string, FC<SvgIconProps> | typeof SvgIcon> = {
  message: MessageIcon,
  notes: NoteIcon,
  events: EventNoteIcon,
  'transport-plan-updates': PlaceIcon,
}

type FilterButtonProps = {
  text: string
  onClick: () => void
  icon: ReactNode
  className: string
}

const FilterButton: FC<FilterButtonProps> = ({ icon, text, ...buttonProps }) => {
  const theme = useTheme()
  const upMedium = useMediaQuery(theme.breakpoints.up('md'))
  return upMedium ? (
    <Button {...buttonProps} startIcon={icon} title={text}>
      {text}
    </Button>
  ) : (
    <IconButton {...buttonProps} title={text}>
      {icon}
    </IconButton>
  )
}

type ActivityFiltersProps = {
  filterModel: TActivityFiltersModel
  onFilterChange: () => void
}

export const ActivityFilters: FC<ActivityFiltersProps> = ({ filterModel, onFilterChange }) => {
  const { translate } = useI18n()
  const classes = useStyles()

  const onFilterClick = (filterId: string) => {
    const { selectedFilters } = filterModel
    if (selectedFilters.length === 1 && selectedFilters[0].id === filterId) {
      return
    }
    filterModel.toggleFilter(filterId)
    onFilterChange()
  }

  return useObserver(() => (
    <Grid
      container
      alignItems="center"
      className={classes.container}
      data-userlane-id="shipment-activityfeed-filters"
      data-tracking-id="shipment-activity_feed-filters"
    >
      <Grid className={classes.filterHelpText}>{translate('activity-feed_filters_title')}</Grid>
      <Grid container className={classes.filterButtons}>
        {filterModel.filters.map((filter) => {
          const FilterIcon = FilterIconMap[filter.id]
          return (
            <Grid key={filter.id}>
              <FilterButton
                onClick={() => onFilterClick(filter.id)}
                className={cn(classes.filterButton, { [classes.unselected]: !filter.selected })}
                data-tracking-id={filter.trackingId}
                text={translate(filter.label)}
                icon={<FilterIcon />}
              />
            </Grid>
          )
        })}
      </Grid>
    </Grid>
  ))
}
